import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FileText, AlertTriangle, CheckCircle, Clock, DollarSign,
  Loader2, Calendar, User, ArrowRight, ExternalLink, Search,
  TrendingUp, TrendingDown, Filter
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const paymentMethods = ["Cash", "Bank Transfer", "Mobile Money", "Card", "Check", "Other"];
const paymentDestinations = [
  { value: "cash", label: "Cash at Office" },
  { value: "bank", label: "Bank Account" },
  { value: "mobile_money", label: "Mobile Money" },
];

export default function AccountsReceivable() {
  const { t } = useLanguage();
  const [arData, setArData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Payment dialog
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paid_date: new Date().toISOString().split("T")[0],
    payment_method: "Bank Transfer",
    payment_destination: "bank",
    paid_amount: ""
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadARData();
  }, []);

  const loadARData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/accounts-receivable`);
      setArData(response.data);
    } catch (error) {
      console.error("Failed to load A/R data:", error);
      toast.error("Failed to load Accounts Receivable data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const openPayDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      paid_date: new Date().toISOString().split("T")[0],
      payment_method: "Bank Transfer",
      payment_destination: "bank",
      paid_amount: invoice.outstanding_amount.toString()
    });
    setShowPayDialog(true);
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/invoices/${selectedInvoice.id}/mark-paid`, {
        paid_date: paymentForm.paid_date,
        payment_method: paymentForm.payment_method,
        payment_destination: paymentForm.payment_destination,
        paid_amount: parseFloat(paymentForm.paid_amount) || selectedInvoice.outstanding_amount
      });
      
      toast.success("Payment confirmed - A/R cleared");
      setShowPayDialog(false);
      setSelectedInvoice(null);
      loadARData();
    } catch (error) {
      console.error("Failed to mark as paid:", error);
      toast.error(error.response?.data?.detail || "Failed to confirm payment");
    } finally {
      setProcessing(false);
    }
  };

  // Filter receivables
  const filteredReceivables = arData?.receivables?.filter(r => {
    const matchesSearch = !searchQuery || 
      r.doc_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === "all" ||
      (filterStatus === "overdue" && r.is_overdue) ||
      (filterStatus === "current" && !r.is_overdue);
    
    return matchesSearch && matchesFilter;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="ar-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-receivable-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Accounts Receivable</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track outstanding Payment Invoices
          </p>
        </div>
        <Link to="/dashboard/tools/payment-invoice">
          <Button className="gap-2" data-testid="new-payment-invoice-btn">
            <FileText className="w-4 h-4" />
            New Payment Invoice
          </Button>
        </Link>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border rounded-lg p-4" data-testid="total-ar-card">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <DollarSign className="w-4 h-4" />
            Total Receivables
          </div>
          <p className="text-2xl font-bold mt-2">
            {formatCurrency(arData?.summary?.total_receivables || 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {arData?.summary?.count || 0} invoice{arData?.summary?.count !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="bg-card border rounded-lg p-4" data-testid="current-ar-card">
          <div className="flex items-center gap-2 text-emerald-600 text-sm">
            <TrendingUp className="w-4 h-4" />
            Current (Not Due)
          </div>
          <p className="text-2xl font-bold mt-2 text-emerald-600">
            {formatCurrency(arData?.summary?.total_current || 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {(arData?.summary?.count || 0) - (arData?.summary?.overdue_count || 0)} invoice{(arData?.summary?.count || 0) - (arData?.summary?.overdue_count || 0) !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="bg-card border rounded-lg p-4" data-testid="overdue-ar-card">
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertTriangle className="w-4 h-4" />
            Overdue
          </div>
          <p className="text-2xl font-bold mt-2 text-red-600">
            {formatCurrency(arData?.summary?.total_overdue || 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {arData?.summary?.overdue_count || 0} invoice{arData?.summary?.overdue_count !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="ar-info-card">
          <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
            <FileText className="w-4 h-4" />
            What is A/R?
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Accounts Receivable tracks money owed to you by customers for Payment Invoices you've issued.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by invoice # or client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="ar-search-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40" data-testid="ar-filter-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="current">Current</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Receivables Table */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="ar-table">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">Invoice #</th>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">Client</th>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">Issue Date</th>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">Due Date</th>
                <th className="text-right p-3 text-xs font-medium text-muted-foreground">Total</th>
                <th className="text-right p-3 text-xs font-medium text-muted-foreground">Outstanding</th>
                <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                <th className="text-right p-3 text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredReceivables.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-muted-foreground">
                    {searchQuery || filterStatus !== "all" 
                      ? "No matching receivables found"
                      : "No outstanding receivables. Create a Payment Invoice to track amounts owed."}
                  </td>
                </tr>
              ) : (
                filteredReceivables.map((receivable) => (
                  <tr key={receivable.id} className="hover:bg-muted/30" data-testid={`ar-row-${receivable.id}`}>
                    <td className="p-3">
                      <Link 
                        to={`/dashboard/library?doc=${receivable.id}`}
                        className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        {receivable.doc_number}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{receivable.client_name || "—"}</span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {formatDate(receivable.issue_date)}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className={`text-sm ${receivable.is_overdue ? 'text-red-600 font-medium' : ''}`}>
                          {formatDate(receivable.due_date)}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-right text-sm">
                      {formatCurrency(receivable.total_amount, receivable.currency)}
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-sm font-semibold">
                        {formatCurrency(receivable.outstanding_amount, receivable.currency)}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {receivable.is_overdue ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <AlertTriangle className="w-3 h-3" />
                          {receivable.days_overdue}d overdue
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <Clock className="w-3 h-3" />
                          {receivable.days_until_due}d left
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPayDialog(receivable)}
                        className="gap-1"
                        data-testid={`pay-btn-${receivable.id}`}
                      >
                        <CheckCircle className="w-3 h-3" />
                        Record Payment
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accounting Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">📊 Accounting Flow</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-700">
          <div>
            <p className="font-medium">1. Payment Invoice Issued</p>
            <p className="text-xs mt-1">Dr Accounts Receivable<br/>Cr Revenue</p>
          </div>
          <div>
            <p className="font-medium">2. Payment Received</p>
            <p className="text-xs mt-1">Dr Cash/Bank/Mobile Money<br/>Cr Accounts Receivable</p>
          </div>
          <div>
            <p className="font-medium">3. A/R Balance</p>
            <p className="text-xs mt-1">Outstanding invoices = Asset on Balance Sheet</p>
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="sm:max-w-md" data-testid="ar-payment-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Payment Received</DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-sm font-medium">{selectedInvoice.doc_number}</p>
                <p className="text-xs text-muted-foreground">{selectedInvoice.client_name || "No client"}</p>
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-muted-foreground">Outstanding:</span>
                  <span className="text-lg font-semibold">
                    {formatCurrency(selectedInvoice.outstanding_amount, selectedInvoice.currency)}
                  </span>
                </div>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Payment date</Label>
                <Input
                  type="date"
                  value={paymentForm.paid_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paid_date: e.target.value })}
                  className="mt-1"
                  data-testid="ar-paid-date-input"
                />
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Payment destination</Label>
                <Select 
                  value={paymentForm.payment_destination} 
                  onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_destination: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="ar-payment-destination-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentDestinations.map(dest => (
                      <SelectItem key={dest.value} value={dest.value}>{dest.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Payment method</Label>
                <Select 
                  value={paymentForm.payment_method} 
                  onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="ar-payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(method => (
                      <SelectItem key={method} value={method}>{method}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Amount received</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={paymentForm.paid_amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paid_amount: e.target.value })}
                    className="pl-7"
                    data-testid="ar-paid-amount-input"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                <p className="font-medium">Accounting Entry:</p>
                <p>Dr {paymentForm.payment_destination === 'cash' ? 'Cash at Office' : paymentForm.payment_destination === 'bank' ? 'Bank Account' : 'Mobile Money'}</p>
                <p>Cr Accounts Receivable</p>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowPayDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleMarkPaid} disabled={processing} data-testid="ar-confirm-payment">
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Confirm payment
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
